import { useState, useEffect, useContext } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Users, Star, Clock, Calendar, ChevronLeft, ChevronRight, Video, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "@/App";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

export default function Consultants() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [booking, setBooking] = useState(false);
  const [calendarConnected, setCalendarConnected] = useState(false);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const { data } = await api.get("/services");
      setServices(data);
    } catch (error) {
      toast.error("Failed to fetch consultants");
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableSlots = async (consultantId, date) => {
    setLoadingSlots(true);
    setSelectedSlot(null);
    try {
      const formattedDate = date.toISOString().split('T')[0];
      const duration = selectedService?.duration_minutes || 60;
      const { data } = await api.get(`/oauth/calendar/available-slots/${consultantId}?date=${formattedDate}&duration_minutes=${duration}`);
      setAvailableSlots(data.slots || []);
      setCalendarConnected(data.calendar_connected || false);
    } catch (error) {
      console.error("Failed to fetch slots:", error);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleOpenBookingDialog = (service) => {
    if (!user) {
      toast.error("Please login to book a consultation");
      navigate("/auth");
      return;
    }
    setSelectedService(service);
    setSelectedDate(new Date());
    setSelectedSlot(null);
    setBookingDialogOpen(true);
    // Fetch slots for today
    fetchAvailableSlots(service.consultant_id, new Date());
  };

  const handleDateChange = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + direction);
    // Don't allow past dates
    if (newDate < new Date().setHours(0, 0, 0, 0)) return;
    setSelectedDate(newDate);
    fetchAvailableSlots(selectedService.consultant_id, newDate);
  };

  const handleBooking = async () => {
    if (!selectedSlot) {
      toast.error("Please select a time slot");
      return;
    }

    setBooking(true);
    try {
      const { data } = await api.post("/oauth/calendar/book", {
        consultant_id: selectedService.consultant_id,
        service_id: selectedService.id,
        start_time: selectedSlot.start,
        end_time: selectedSlot.end,
        title: `Consultation: ${selectedService.title}`,
        description: `Booked via ReBusi`
      });
      
      toast.success(data.message || "Booking confirmed!");
      
      if (data.meeting_link) {
        toast.success("Google Meet link has been sent to your email!", {
          duration: 5000
        });
      }
      
      setBookingDialogOpen(false);
      setSelectedSlot(null);
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Booking failed. Please try again.");
    } finally {
      setBooking(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatSlotTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-IN', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatDateDisplay = (date) => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return "Tomorrow";
    }
    return date.toLocaleDateString('en-IN', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-12" data-testid="consultants-header">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Expert Consultants</h1>
          <p className="text-lg text-slate-600">Book consultation sessions with verified business experts</p>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : services.length === 0 ? (
          <div className="text-center py-12" data-testid="no-consultants">
            <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">No consultants available</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="consultants-grid">
            {services.map((service) => (
              <Card key={service.id} className="flex flex-col items-center bg-white border border-slate-100 rounded-2xl p-6 hover:border-blue-100 hover:shadow-lg transition-all" data-testid={`consultant-card-${service.id}`}>
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-white">{service.consultant_name.charAt(0)}</span>
                </div>
                
                <h3 className="text-xl font-semibold text-slate-900 mb-1 text-center">{service.consultant_name}</h3>
                <p className="text-sm text-slate-500 mb-4">{service.title}</p>
                
                <div className="flex items-center gap-1 mb-4">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  <span className="text-sm font-medium">{service.rating.toFixed(1)}</span>
                  <span className="text-sm text-slate-500">({service.total_bookings} bookings)</span>
                </div>

                <div className="flex flex-wrap gap-2 justify-center mb-4">
                  {service.expertise_tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                  ))}
                </div>

                <p className="text-sm text-slate-600 mb-4 text-center line-clamp-3">{service.description}</p>

                <div className="mt-auto w-full space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-1 text-slate-600">
                      <Clock className="w-4 h-4" />
                      {service.duration_minutes} min
                    </div>
                    <span className="text-2xl font-bold text-slate-900">{formatCurrency(service.price)}</span>
                  </div>
                  
                  <Button 
                    className="w-full bg-blue-600 hover:bg-blue-700" 
                    data-testid={`book-consultation-${service.id}`}
                    onClick={() => handleOpenBookingDialog(service)}
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Book Consultation
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Booking Dialog with Calendar Slots */}
      <Dialog open={bookingDialogOpen} onOpenChange={setBookingDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              Book Consultation
            </DialogTitle>
            <DialogDescription>
              Schedule your session with {selectedService?.consultant_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Service Info */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-900">{selectedService?.title}</p>
                <p className="text-sm text-slate-500">{selectedService?.duration_minutes} minutes</p>
              </div>
              <p className="text-lg font-bold text-blue-600">{formatCurrency(selectedService?.price || 0)}</p>
            </div>

            {/* Date Navigation */}
            <div className="flex items-center justify-between">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleDateChange(-1)}
                disabled={isToday(selectedDate)}
                data-testid="prev-date-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="text-center">
                <p className="font-semibold text-slate-900">{formatDateDisplay(selectedDate)}</p>
                <p className="text-xs text-slate-500">
                  {selectedDate.toLocaleDateString('en-IN', { month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleDateChange(1)}
                data-testid="next-date-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Calendar Connection Status */}
            {!loadingSlots && calendarConnected && (
              <div className="flex items-center gap-2 text-xs text-emerald-600 bg-emerald-50 px-3 py-2 rounded-lg">
                <Check className="w-3 h-3" />
                Showing real-time availability from consultant's calendar
              </div>
            )}

            {/* Time Slots */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-700">Available Time Slots</p>
              {loadingSlots ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : availableSlots.filter(s => s.available).length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Calendar className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <p>No available slots for this date</p>
                  <p className="text-xs">Try selecting another date</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2 max-h-[200px] overflow-y-auto" data-testid="time-slots-grid">
                  {availableSlots.filter(slot => slot.available).map((slot, idx) => (
                    <Button
                      key={idx}
                      variant={selectedSlot?.start === slot.start ? "default" : "outline"}
                      size="sm"
                      className={`text-xs ${selectedSlot?.start === slot.start ? 'bg-blue-600' : ''}`}
                      onClick={() => setSelectedSlot(slot)}
                      data-testid={`slot-${idx}`}
                    >
                      {formatSlotTime(slot.start)}
                    </Button>
                  ))}
                </div>
              )}
            </div>

            {/* Selected Slot Info */}
            {selectedSlot && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-sm text-blue-800">
                  <strong>Selected:</strong> {formatDateDisplay(selectedDate)} at {formatSlotTime(selectedSlot.start)} - {formatSlotTime(selectedSlot.end)}
                </p>
                <div className="flex items-center gap-1 mt-2 text-xs text-blue-600">
                  <Video className="w-3 h-3" />
                  Google Meet link will be sent to your email
                </div>
              </div>
            )}

            {/* Book Button */}
            <Button 
              className="w-full bg-blue-600 hover:bg-blue-700" 
              disabled={!selectedSlot || booking}
              onClick={handleBooking}
              data-testid="confirm-booking-button"
            >
              {booking ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Booking...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Confirm Booking
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
